import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, Subject, catchError, debounceTime, finalize, of, switchMap, tap } from 'rxjs';
import { ClientesApiService } from '../api/clientes-api.service';
import {
  Cliente,
  ClienteFormValue,
  ConsultaClientes,
  CuentaInclub,
  PAGINA_VACIA,
  PaginaClientes,
  ResultadoImportacion,
  TipoCliente,
} from '../models/clientes.model';

@Injectable({ providedIn: 'root' })
export class ClientesService {
  private readonly api = inject(ClientesApiService);

  readonly pagina = signal<PaginaClientes>(PAGINA_VACIA);
  readonly cargando = signal(false);
  readonly error = signal<string | null>(null);

  private readonly consultas$ = new Subject<ConsultaClientes>();
  private ultimaConsulta: ConsultaClientes | null = null;

  constructor() {
    this.consultas$
      .pipe(
        debounceTime(250),
        switchMap((consulta) => this.cargar(consulta)),
      )
      .subscribe((pagina) => this.pagina.set(pagina));
  }

  buscar(consulta: ConsultaClientes): void {
    this.ultimaConsulta = consulta;
    this.consultas$.next(consulta);
  }

  recargar(): void {
    if (this.ultimaConsulta) {
      this.consultas$.next(this.ultimaConsulta);
    }
  }

  crear(form: ClienteFormValue): Observable<Cliente> {
    return this.api.crear(form).pipe(tap(() => this.recargar()));
  }

  actualizar(id: string, form: ClienteFormValue): Observable<Cliente> {
    return this.api.actualizar(id, form).pipe(tap(() => this.recargar()));
  }

  buscarSocio(numeroDocumento: string): Observable<CuentaInclub> {
    return this.api.buscarSocio(numeroDocumento);
  }

  crearSocio(form: ClienteFormValue): Observable<Cliente> {
    return this.api.crearSocio(form).pipe(tap(() => this.recargar()));
  }

  sincronizarSocio(id: string): Observable<Cliente> {
    return this.api.sincronizarSocio(id).pipe(tap(() => this.recargar()));
  }

  descargarPlantilla(tipo: TipoCliente, incluirClientes: boolean): Observable<Blob> {
    return this.api.descargarPlantilla(tipo, incluirClientes);
  }

  importar(tipo: TipoCliente, archivo: File): Observable<ResultadoImportacion> {
    return this.api.importar(tipo, archivo).pipe(tap(() => this.recargar()));
  }

  private cargar(consulta: ConsultaClientes): Observable<PaginaClientes> {
    this.error.set(null);
    this.cargando.set(true);
    return this.api.buscar(consulta).pipe(
      catchError((error) => {
        this.error.set(mensajeDeError(error));
        return of(PAGINA_VACIA);
      }),
      finalize(() => this.cargando.set(false)),
    );
  }
}

export function mensajeDeError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return 'No se pudo conectar con el servicio de clientes. Revisa tu conexión e intenta de nuevo';
    }
    const mensaje = (error.error as { message?: string } | null)?.message;
    if (mensaje) {
      return mensaje;
    }
  }
  return 'Ocurrió un error inesperado. Intenta de nuevo';
}
